import { action, computed, observable, runInAction } from 'mobx';
import { isNil } from 'lodash';
import { UserBasicDetailsDto } from '../types';
import { UsersService } from '../services';
import { AppPermissions } from '../../authorization/Permissions';

export default class UsersStore {
    private static readonly LOOKUP_RETRY_AFTER_MS = 30000;

    @observable
    users: UserBasicDetailsDto[] = [];

    readonly canManageUsers: boolean;

    @observable
    private pendingDirectoryRequests = 0;

    private directoryRequest?: Promise<void>;
    private readonly pendingUsers = new Map<string, Promise<void>>();
    private readonly missingIds = new Set<string>();
    private readonly queuedIds = new Set<string>();
    private readonly failedAt = new Map<string, number>();

    @computed({ keepAlive: true })
    get usersById() {
        return new Map(this.users.map(user => [user.id, user]));
    }

    @computed
    get isLoading() {
        // Background name resolution must not hide already loaded records.
        return this.pendingDirectoryRequests > 0;
    }

    constructor(
        private usersService: UsersService,
        permissions: string[] = []
    ) {
        this.canManageUsers = permissions.includes(AppPermissions.CanManageUsers);
        void this.getUsers();
    }

    @action.bound
    getUsers(): Promise<void> {
        if (!this.canManageUsers) {
            return Promise.resolve();
        }
        if (!this.directoryRequest) {
            this.directoryRequest = this.fetchDirectory();
        }
        return this.directoryRequest;
    }

    async loadDisplayUsers(userIds: Array<string | null | undefined>): Promise<void> {
        // The directory may already contain these users. A failed directory read
        // still permits explicit-ID lookups once that request has settled.
        if (this.directoryRequest) {
            await this.directoryRequest;
        }
        const ids = Array.from(new Set(userIds.filter((id): id is string => !!id?.trim())));
        const pending = ids.map(id => this.pendingUsers.get(id)).filter(Boolean);
        const usersById = this.usersById;
        const missing = ids.filter(
            id =>
                !this.missingIds.has(id) &&
                !this.isLookupCoolingDown(id) &&
                !usersById.has(id) &&
                !this.pendingUsers.has(id)
        );
        if (missing.length) {
            const request = this.fetchDisplayUsers(missing);
            missing.forEach(id => this.pendingUsers.set(id, request));
            pending.push(request);
        }
        await Promise.all(pending);
    }

    getUserById(id: string): UserBasicDetailsDto | undefined {
        const user = this.usersById.get(id);
        if (
            !user &&
            id?.trim() &&
            !this.missingIds.has(id) &&
            !this.isLookupCoolingDown(id) &&
            !this.pendingUsers.has(id)
        ) {
            this.queueLookup(id);
        }
        return user;
    }

    getUserNameById(id: string) {
        const user = this.getUserById(id);
        if (!user) {
            return id;
        }
        return !isNil(user.firstName) && !isNil(user.lastName) ? `${user.firstName} ${user.lastName}` : user.userName;
    }

    resolveUsers(ids: Iterable<string | null | undefined>): UserBasicDetailsDto[] {
        if (this.canManageUsers) {
            return this.users;
        }
        return Array.from(new Set(Array.from(ids).filter((id): id is string => !!id?.trim())))
            .map(id => this.getUserById(id))
            .filter((user): user is UserBasicDetailsDto => !!user);
    }

    private async fetchDirectory(): Promise<void> {
        runInAction(() => this.pendingDirectoryRequests++);
        try {
            const users = await this.usersService.getUsersBasicDetails();
            this.missingIds.clear();
            runInAction(() => (this.users = users));
        } catch (error) {
            console.error('Failed to load user directory', error);
        } finally {
            this.directoryRequest = undefined;
            runInAction(() => this.pendingDirectoryRequests--);
        }
    }

    private async fetchDisplayUsers(ids: string[]): Promise<void> {
        try {
            const { users, failedIds, errors } = await this.usersService.getDisplayUsers(ids);
            errors.forEach(error => console.error('Failed to load user display names', error));
            const failedAt = Date.now();
            failedIds.forEach(id => this.failedAt.set(id, failedAt));
            const returnedIds = new Set(users.map(user => user.id));
            const failed = new Set(failedIds);
            ids.filter(id => !failed.has(id)).forEach(id => this.failedAt.delete(id));
            ids.filter(id => !failed.has(id) && !returnedIds.has(id)).forEach(id => this.missingIds.add(id));
            if (users.length) {
                runInAction(() => {
                    const merged = new Map(this.usersById);
                    users.forEach(user => {
                        merged.set(user.id, user);
                    });
                    this.users = Array.from(merged.values());
                });
            }
        } catch (error) {
            const failedAt = Date.now();
            ids.forEach(id => this.failedAt.set(id, failedAt));
            console.error('Failed to load user display names', error);
        } finally {
            ids.forEach(id => this.pendingUsers.delete(id));
        }
    }

    private isLookupCoolingDown(id: string) {
        const failed = this.failedAt.get(id);
        return !!failed && Date.now() - failed < UsersStore.LOOKUP_RETRY_AFTER_MS;
    }

    private queueLookup(id: string) {
        if (this.queuedIds.has(id) || this.isLookupCoolingDown(id)) {
            return;
        }
        this.queuedIds.add(id);
        if (this.queuedIds.size === 1) {
            void Promise.resolve().then(() => {
                const ids = Array.from(this.queuedIds);
                this.queuedIds.clear();
                return this.loadDisplayUsers(ids);
            });
        }
    }
}
